import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { AlertTriangle, Eye, PanelsTopLeft } from "lucide-react";
import { useIntegrations, useUpdateWidgetConfig, useWidgetEmbed } from "../hooks/useApi";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "@/hooks/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import WidgetPreview from "../components/WidgetPreview";
import ProductPreviewPage from "./ProductPreviewPage";
import { BrandCard, BrandStyles, SPAZI, VARIABILI_SEZIONE } from "../components/integrations/brand";
import { CorniceAnteprima } from "../components/integrations/cornice";
import { BarraSalvataggio, GRIGIO, IntestazioneCasella, Interruttore } from "../components/integrations/casella";
import {
  FONT,
  PRESET,
  SezioneAspetto,
  SezioneComparsa,
  SezionePosizione,
  SezioneStrumenti,
  SezioneTesti,
  SezioneTipo,
  TESTI_PREDEFINITI,
} from "../components/widget/sezioni";

// Kill-switch temporaneo: l'opzione "Barra di ricerca" si puo' nascondere
// (richiesta del management). Con false spariscono la scelta e le
// impostazioni della barra.
const SEARCHBAR_UI_ENABLED = true;

/**
 * La configurazione salvata sopra i predefiniti, in profondita': uno spread
 * semplice sostituirebbe intere sezioni (theme/position/behavior) perdendo le
 * chiavi nuove che le configurazioni vecchie non hanno. Le liste si
 * sostituiscono.
 */
export const mergeWidgetConfig = (defaults, saved) => {
  if (!saved || typeof saved !== "object") return defaults;
  const result = { ...defaults };
  for (const key of Object.keys(saved)) {
    const savedVal = saved[key];
    const defVal = defaults?.[key];
    if (savedVal && typeof savedVal === "object" && !Array.isArray(savedVal) && defVal && typeof defVal === "object" && !Array.isArray(defVal)) {
      result[key] = mergeWidgetConfig(defVal, savedVal);
    } else if (savedVal !== undefined) {
      result[key] = savedVal;
    }
  }
  return result;
};

/**
 * I predefiniti, uguali a quelli del server (routes/widget.js): salvare una
 * impostazione qualsiasi non deve cambiare cio' che il visitatore vede. Prima
 * il benvenuto predefinito era in inglese qui e in italiano sul server, e il
 * primo salvataggio lo portava in inglese.
 */
export const predefiniti = (bot) => ({
  welcomeMessage: `Ciao 👋 sono ${bot?.name || "il tuo assistente"}, come posso aiutarti?`,
  title: bot?.name || "Assistente",
  subtitle: TESTI_PREDEFINITI.subtitle,
  placeholder: TESTI_PREDEFINITI.placeholder,
  mode: "widget",
  theme: {
    primaryColor: "#2563eb",
    secondaryColor: "#64748b",
    accentColor: "#0f172a",
    backgroundColor: "#ffffff",
    textColor: "#1f2937",
    gradientEnabled: false,
    gradientDirection: 45,
    gradientColors: ["#2563eb", "#8b5cf6"],
    fontFamily: "Inter",
    fontSize: 14,
    borderRadius: 8,
    spacing: 16,
    logoUrl: "",
    logoHeight: 48,
    logoPosition: "left",
    buttonIcon: "pingup",
    buttonStyle: "icon", // "icon" = bolla con icona, "mascotte3d" = mascotte 3D PingUp
    shadowIntensity: 4,
    animationStyle: "fade",
    darkMode: false,
  },
  position: {
    position: "bottom-right",
    offsetX: 20,
    offsetY: 20,
    zIndex: 1000,
    size: "medium",
    mobilePosition: "bottom-center",
    mobileSize: "fullscreen",
  },
  behavior: {
    autoOpen: false,
    autoOpenDelay: 3000,
    autoOpenTrigger: "time",
    scrollPercentage: 50,
    showOnPages: "all",
    excludePages: "",
    includePaths: "",
    triggers: [],
    customCSS: "",
    soundEnabled: true,
    notifications: true,
    typing: true,
    readReceipts: true,
    fileUpload: false,
    emojiPicker: true,
    quickReplies: ["Hello", "I need help", "Contact support"],
    smartQuickReplies: { enabled: false, delay: 5000, keepClosed: false },
    externalCTA: {
      enabled: false,
      type: "message",
      message: "Hai bisogno di aiuto?",
      // Pillola WhatsApp: testo e messaggio stanno nel widget, il numero
      // arriva dall'app WhatsApp.
      whatsappText: "Scrivici su WhatsApp",
      whatsappMessage: "Ho bisogno di aiuto",
      showDelay: 5000,
      autoHide: true,
      autoHideDelay: 15000,
    },
    businessHours: {
      enabled: false,
      timezone: "UTC",
      monday: { enabled: true, start: "09:00", end: "17:00" },
      tuesday: { enabled: true, start: "09:00", end: "17:00" },
      wednesday: { enabled: true, start: "09:00", end: "17:00" },
      thursday: { enabled: true, start: "09:00", end: "17:00" },
      friday: { enabled: true, start: "09:00", end: "17:00" },
      saturday: { enabled: false, start: "09:00", end: "17:00" },
      sunday: { enabled: false, start: "09:00", end: "17:00" },
    },
    offline: {
      enabled: true,
      message: "In questo momento non siamo disponibili. Lasciaci un messaggio e ti rispondiamo appena possibile.",
      formEnabled: true,
    },
  },
  integrations: {
    googleAnalytics: "",
    facebookPixel: "",
    customEvents: true,
    webhooks: { onMessage: "", onStart: "", onEnd: "" },
  },
  preset: "default",
});

// Carica un Google Font quando lo si sceglie (i font di sistema no).
const caricaFont = (nome) => {
  const font = FONT.find((f) => f.value === nome);
  if (!font || font.type === "system") return;
  const id = `google-font-${nome.replace(/\s+/g, "-").toLowerCase()}`;
  if (document.getElementById(id)) return;
  const link = document.createElement("link");
  link.id = id;
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${encodeURIComponent(nome)}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
};

/** Imposta `valore` al percorso "a.b.c", copiando gli oggetti lungo la strada (l'anteprima vede il cambio). */
function conValore(config, percorso, valore) {
  const chiavi = percorso.split(".");
  const nuova = { ...config };
  let dove = nuova;
  for (const chiave of chiavi.slice(0, -1)) {
    dove[chiave] = dove[chiave] && typeof dove[chiave] === "object" ? { ...dove[chiave] } : {};
    dove = dove[chiave];
  }
  dove[chiavi.at(-1)] = valore;
  return nuova;
}

/**
 * Il widget del bot, in schede che seguono il ragionamento: che cosa mettere
 * sul sito, cosa dice, come appare, dove sta, quando compare, gli strumenti
 * della barra, e una prova. A destra l'anteprima dal vivo; in basso la barra
 * per salvare, sempre in vista.
 */
export default function WidgetTab({ bot }) {
  const { id: botId } = useParams();
  if (!bot) {
    return (
      <div className="mx-auto max-w-[1400px] space-y-4 pt-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-[480px] rounded-[14px]" />
      </div>
    );
  }
  // Passando da un bot all'altro la pagina riparte da capo.
  return <PaginaWidget key={bot._id} bot={bot} botId={botId} />;
}

function PaginaWidget({ bot, botId }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const salvataggio = useUpdateWidgetConfig();
  const { data: embed, refetch: rileggiEmbed } = useWidgetEmbed(botId);
  const { data: integrazioni, isLoading: integrazioniInCaricamento } = useIntegrations(botId);
  const numeroWhatsapp = integrazioni?.whatsapp?.enabled ? integrazioni.whatsapp.phone || "" : "";

  const [config, setConfig] = useState(() => mergeWidgetConfig(predefiniti(bot), bot.widget_config));
  // L'ultima versione salvata: da qui "modificato" e "Annulla".
  const salvata = useRef(JSON.stringify(config));
  const modificato = JSON.stringify(config) !== salvata.current;

  // Se i dati del bot arrivano di nuovo (dopo un salvataggio, o quando la
  // dashboard li rilegge) si prendono solo se non ci sono modifiche in corso:
  // prima le modifiche non salvate venivano sovrascritte in silenzio.
  const salvatoSulServer = JSON.stringify(bot.widget_config || null);
  useEffect(() => {
    if (modificato) return;
    const dalServer = mergeWidgetConfig(predefiniti(bot), bot.widget_config);
    salvata.current = JSON.stringify(dalServer);
    setConfig(dalServer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [salvatoSulServer]);

  // Uscendo dalla pagina con modifiche non salvate, il browser chiede conferma.
  useEffect(() => {
    if (!modificato) return undefined;
    const avvisa = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", avvisa);
    return () => window.removeEventListener("beforeunload", avvisa);
  }, [modificato]);

  useEffect(() => {
    if (config.theme?.fontFamily) caricaFont(config.theme.fontFamily);
  }, [config.theme?.fontFamily]);

  const cambia = (percorso, valore) => setConfig((prima) => conValore(prima, percorso, valore));
  const applicaPreset = (nome) => setConfig((prima) => ({ ...prima, theme: { ...prima.theme, ...PRESET[nome] } }));

  const salva = async () => {
    try {
      await salvataggio.mutateAsync({ botId, widgetConfig: config });
      salvata.current = JSON.stringify(config);
      setConfig((c) => ({ ...c }));
      rileggiEmbed();
      toast({ variant: "success", title: t("common.changesSaved", "Modifiche salvate") });
    } catch (error) {
      console.error("Configurazione del widget non salvata:", error);
      toast({ variant: "destructive", title: t("common.error", "Errore") });
    }
  };
  const annulla = () => setConfig(JSON.parse(salvata.current));

  const codice = embed?.widget_code || bot.widget_code || "";
  const copiaCodice = () => {
    if (!codice) return;
    navigator.clipboard.writeText(codice);
    toast({ variant: "success", title: t("widget.basic.widgetCodeCopied", "Codice copiato") });
  };

  // Le schede: quelle che non valgono per il tipo scelto non ci sono.
  const barra = SEARCHBAR_UI_ENABLED && config.mode === "searchbar";
  const schede = [
    { valore: "tipo", etichetta: t("widget.tabs.type", "Tipo") },
    { valore: "testi", etichetta: t("widget.tabs.texts", "Testi") },
    { valore: "aspetto", etichetta: t("widget.tabs.look", "Aspetto") },
    !barra && { valore: "posizione", etichetta: t("widget.tabs.position", "Posizione") },
    { valore: "comparsa", etichetta: t("widget.tabs.appear", "Quando compare") },
    barra && { valore: "strumenti", etichetta: t("widget.tabs.tools", "Strumenti") },
    { valore: "prova", etichetta: t("widget.tabs.try", "Prova") },
  ].filter(Boolean);
  const [scheda, setScheda] = useState("tipo");
  const schedaAttiva = schede.some((s) => s.valore === scheda) ? scheda : "tipo";

  // Anteprima: il numero WhatsApp lo aggiunge il server al widget vero; qui
  // lo si aggiunge a mano, se no la pillola WhatsApp non si vedrebbe mai.
  const [dispositivo, setDispositivo] = useState("desktop");
  const [prodottoAnteprima, setProdottoAnteprima] = useState(null);
  const [demo, setDemo] = useState({});
  const configAnteprima = useMemo(
    () =>
      numeroWhatsapp
        ? { ...config, whatsapp: { enabled: true, phone_number_id: numeroWhatsapp, product_action: integrazioni?.whatsapp?.product_action || "learn-more" } }
        : config,
    [config, numeroWhatsapp, integrazioni?.whatsapp?.product_action]
  );

  const props = { config, cambia, t, barra, botId };

  return (
    <div className="relative isolate" style={VARIABILI_SEZIONE}>
      <BrandStyles />

      <IntestazioneCasella
        icona={PanelsTopLeft}
        titolo={t("widget.tabs.pageTitle", "Widget")}
        dettagli={
          <span className="text-sm text-[#64748B]">
            {barra ? t("widget.basic.modeSearchbar", "Barra di ricerca") : t("widget.basic.modeWidget", "Chat widget")}
          </span>
        }
        azioni={
          <button
            type="button"
            className={GRIGIO}
            disabled={!embed?.preview_url}
            onClick={() => window.open(embed.preview_url, "_blank", "noopener")}
            title={t("widget.sections.savedPreviewHint", "Apre il widget salvato in una nuova scheda")}
          >
            <Eye className="h-3.5 w-3.5" aria-hidden="true" />
            {t("widget.livePreview", "Anteprima salvata")}
          </button>
        }
      />

      <div className={`pu-fade-up mx-auto grid max-w-[1400px] items-start ${SPAZI.campiAnteprima} pb-10 xl:grid-cols-[minmax(0,1fr)_420px] 2xl:grid-cols-[minmax(0,1fr)_520px]`}>
        <div className="min-w-0 space-y-4">
          <div className="-mx-1 overflow-x-auto px-1 pb-1">
            <Interruttore etichetta={t("widget.tabs.label", "Impostazioni del widget")} valore={schedaAttiva} onCambia={setScheda} voci={schede} />
          </div>

          {salvataggio.error ? (
            <div className="flex gap-3 rounded-[14px] border border-red-200 bg-red-50 p-4">
              <AlertTriangle className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
              <div>
                <p className="text-sm font-medium text-red-800">{t("widget.failedSave", "Impossibile salvare la configurazione")}</p>
                <p className="mt-1 text-sm text-red-700">{salvataggio.error.message}</p>
              </div>
            </div>
          ) : null}

          <div key={schedaAttiva} className={`pu-carta-in ${SPAZI.schedeContenuto}`}>
            {schedaAttiva === "tipo" ? (
              <SezioneTipo {...props} barraAbilitata={SEARCHBAR_UI_ENABLED} codice={codice} onCopiaCodice={copiaCodice} />
            ) : null}
            {schedaAttiva === "testi" ? <SezioneTesti {...props} nomeBot={bot.name} /> : null}
            {schedaAttiva === "aspetto" ? <SezioneAspetto {...props} onPreset={applicaPreset} /> : null}
            {schedaAttiva === "posizione" ? <SezionePosizione {...props} /> : null}
            {schedaAttiva === "comparsa" ? (
              <SezioneComparsa {...props} numeroWhatsapp={numeroWhatsapp} integrazioniInCaricamento={integrazioniInCaricamento} />
            ) : null}
            {schedaAttiva === "strumenti" ? <SezioneStrumenti {...props} /> : null}
            {schedaAttiva === "prova" ? (
              <BrandCard className="p-5 sm:p-6">
                <ProductPreviewPage bot={bot} onProductLoaded={setProdottoAnteprima} demo={demo} onDemoChange={setDemo} />
              </BrandCard>
            ) : null}
          </div>

          {/* Sempre in vista: prima il pulsante stava in cima e spariva scorrendo. */}
          <BarraSalvataggio
            modificato={modificato}
            saving={salvataggio.isPending}
            onSalva={salva}
            onAnnulla={annulla}
            testi={{
              nonSalvate: t("widget.unsavedChanges", "Modifiche non salvate"),
              salvato: t("widget.sections.allSaved", "Tutto salvato"),
              annulla: t("widget.sections.discard", "Annulla le modifiche"),
              salva: t("widget.saveConfiguration", "Salva"),
            }}
          />
        </div>

        {/* Come nelle pagine delle app (e nell'onboarding): la nota a mano con la
            freccia sopra l'anteprima. */}
        <CorniceAnteprima
          id={`widget-preview-${botId}`}
          nota={
            <>
              {t("widget.sections.previewNote1", "così lo vedranno")}
              <br />
              {t("widget.sections.previewNote2", "i tuoi visitatori")}
            </>
          }
          dispositivo={dispositivo}
          onDispositivo={setDispositivo}
          etichette={{
            dispositivo: t("widget.sections.device", "Dispositivo"),
            computer: t("widget.sections.desktop", "Computer"),
            telefono: t("widget.sections.mobile", "Telefono"),
          }}
          piede={t("widget.sections.previewHint", "Le modifiche si vedono subito qui; sul sito arrivano quando salvi.")}
        >
          <WidgetPreview config={configAnteprima} device={dispositivo} product={prodottoAnteprima} demo={demo} />
        </CorniceAnteprima>
      </div>
    </div>
  );
}
